import { useCallback } from "react";
import { api_url } from "@/app/api";
import { fetchWithAuth } from "@/app/lib/authInterceptor";
import { METHOD_TYPE } from "@/app/lib/enums";
import { logger } from "@/app/lib/logger";
import type { ChatMessage } from "@/types";
import {
   CHAT_TIMEOUT_MS,
   RECONNECT_FETCH_TIMEOUT_MS,
   TIMEOUT_CHECK_INTERVAL_MS
} from "./constants";
import {
   buildHistoryFromStreamPayload,
   mergeFeedbackIntoHistory
} from "./historyMappers";
import {
   extractSseMessages,
   isObjectStreamPayload,
   readSseDataLine,
   trimProcessedSseBuffer
} from "./streamParsing";
import type { HistoryViewGuard } from "./useHistoryViewGuard";
import type {
   ChatTranslation,
   SetChatError,
   SetCurrentAttachments,
   SetHistory,
   SetLoading,
   SetStreamingResponse,
   StopChatStreaming,
   StreamRefs,
   UpdateActiveGeneration
} from "./types";

interface UseStreamReconnectOptions extends Omit<StreamRefs, "isSavingRef"> {
   history: ChatMessage[];
   setLoading: SetLoading;
   setStreamingResponse: SetStreamingResponse;
   setError: SetChatError;
   setCurrentAttachments: SetCurrentAttachments;
   setHistory: SetHistory;
   selectedBaseKnowledgeId: string;
   triggerUsageRefresh: (projectId: string) => void;
   stopChatStreaming: StopChatStreaming;
   removeActiveGeneration: UpdateActiveGeneration;
   addActiveGeneration: UpdateActiveGeneration;
   translation: ChatTranslation;
   markCurrentChatUpdated?: () => void;
   viewGuard: HistoryViewGuard;
}

export function useStreamReconnect({
   history,
   currentReader,
   timeoutRef,
   streamingHistoryRef,
   isUnmountingRef,
   isReconnectingRef,
   abortControllerRef,
   setLoading,
   setStreamingResponse,
   setError,
   setCurrentAttachments,
   setHistory,
   selectedBaseKnowledgeId,
   triggerUsageRefresh,
   stopChatStreaming,
   removeActiveGeneration,
   addActiveGeneration,
   translation,
   markCurrentChatUpdated,
   viewGuard
}: UseStreamReconnectOptions) {
   return useCallback(
      async (chatHistoryId: string): Promise<boolean> => {
         logger.info("[StreamReconnect] Attempting reconnect", { chatHistoryId });
         isReconnectingRef.current = true;
         // Set early so cleanup can detect same-chat transition.
         streamingHistoryRef.current = chatHistoryId;
         // Capture the view visible when reconnect starts; drop writes if it changes.
         const scope = viewGuard.capture();
         let signal: AbortSignal | undefined = undefined;

         let isTimeout = false;
         let fetchTimeoutId: ReturnType<typeof setTimeout> | null = null;

         try {
            if (abortControllerRef.current) {
               abortControllerRef.current.abort();
            }
            abortControllerRef.current = new AbortController();
            signal = abortControllerRef.current.signal;

            setStreamingResponse("");
            setError(null);

            fetchTimeoutId = setTimeout(() => {
               isTimeout = true;
               logger.warn("[StreamReconnect] Fetch connection timeout", {
                  chatHistoryId
               });
               abortControllerRef.current?.abort();
            }, RECONNECT_FETCH_TIMEOUT_MS);

            const url = api_url.chat_stream_url.replace("{id}", chatHistoryId);
            const response = await fetchWithAuth(url, {
               method: METHOD_TYPE.GET,
               cache: "no-store",
               signal
            });

            clearTimeout(fetchTimeoutId);

            if (response.status === 404) {
               logger.debug("[StreamReconnect] No active generation (404)", {
                  chatHistoryId
               });
               removeActiveGeneration(chatHistoryId);
               isReconnectingRef.current = false;
               return false;
            }

            if (!response.ok || !response.body) {
               logger.error("[StreamReconnect] Failed to connect", {
                  status: response.status,
                  chatHistoryId
               });
               removeActiveGeneration(chatHistoryId);
               isReconnectingRef.current = false;
               return false;
            }

            setLoading(true);
            addActiveGeneration(chatHistoryId);
            logger.success("[StreamReconnect] Connected, reading SSE stream...");

            const reader = response.body.getReader();
            currentReader.current = reader;
            const decoder = new TextDecoder("utf-8", { fatal: false });
            let buffer = "";
            let lastActivity = Date.now();
            let tokenCount = 0;

            timeoutRef.current = setInterval(() => {
               const inactiveTime = Date.now() - lastActivity;
               if (inactiveTime > CHAT_TIMEOUT_MS) {
                  logger.warn("[StreamReconnect] Timeout — no activity", {
                     chatHistoryId,
                     inactiveMs: inactiveTime
                  });
                  if (timeoutRef.current) clearInterval(timeoutRef.current);
                  timeoutRef.current = null;
                  setLoading(false);
                  reader.cancel();
               }
            }, TIMEOUT_CHECK_INTERVAL_MS);

            try {
               streamingHistoryRef.current = chatHistoryId;

               reconnectStream: while (true) {
                  const { done, value } = await reader.read();

                  if (done) {
                     logger.debug("[StreamReconnect] Stream ended (done=true)", {
                        chatHistoryId,
                        tokenCount
                     });
                     break;
                  }

                  lastActivity = Date.now();
                  const chunk = decoder.decode(value, { stream: true });
                  buffer += chunk;

                  const messages = extractSseMessages(buffer);
                  if (messages.length > 0) {
                     buffer = trimProcessedSseBuffer(buffer);

                     for (const line of messages) {
                        const json = readSseDataLine(line);
                        if (!json) continue;

                        try {
                           const data = JSON.parse(json);
                           if (!isObjectStreamPayload(data)) continue;

                           if (data.type === "heartbeat") {
                              lastActivity = Date.now();
                              continue;
                           }

                           if (data.token) {
                              scope.run(() =>
                                 setStreamingResponse((prev) => prev + data.token)
                              );
                              tokenCount++;
                           } else if (
                              data.type === "complete" &&
                              data.status === "success"
                           ) {
                              logger.success("[StreamReconnect] Generation complete", {
                                 chatHistoryId,
                                 tokenCount,
                                 historyLength: data.history?.length
                              });

                              const updatedHistory = buildHistoryFromStreamPayload(data);
                              const mergedHistory = mergeFeedbackIntoHistory(
                                 updatedHistory!,
                                 history
                              );

                              scope.run(() => {
                                 setCurrentAttachments(data.attachments || []);
                                 setHistory(mergedHistory);
                                 setStreamingResponse("");
                                 markCurrentChatUpdated?.();
                              });
                           } else if (data.type === "done") {
                              setTimeout(() => {
                                 triggerUsageRefresh(selectedBaseKnowledgeId);
                              }, 1_000);
                              logger.success(
                                 "[StreamReconnect] Stream done, finalizing",
                                 { chatHistoryId }
                              );
                              await stopChatStreaming();
                              setLoading(false);
                              break reconnectStream;
                           } else if (data.type === "error" || data.status === "error") {
                              logger.error("[StreamReconnect] Server error", {
                                 chatHistoryId,
                                 message: data.message
                              });
                              setError(
                                 String(data.message).includes("HISTORY_SUMMARY_PENDING")
                                    ? translation("historySummaryPending")
                                    : data.message || translation("undefinedError")
                              );
                              setLoading(false);
                              break reconnectStream;
                           }
                        } catch (parseError) {
                           logger.error("[StreamReconnect] Parse error:", parseError);
                        }
                     }
                  }
               }
            } finally {
               if (timeoutRef.current) {
                  clearInterval(timeoutRef.current);
                  timeoutRef.current = null;
               }
               currentReader.current = null;
               streamingHistoryRef.current = null;
            }

            return true;
         } catch (error) {
            if (error instanceof Error && error.name === "AbortError") {
               if (isTimeout) {
                  logger.warn("[StreamReconnect] Reconnect aborted due to timeout", {
                     chatHistoryId
                  });
                  removeActiveGeneration(chatHistoryId);
                  setLoading(false);
               } else {
                  logger.debug("[StreamReconnect] Reconnect fetch aborted successfully.");
               }
               return false;
            }
            logger.error("[StreamReconnect] Unexpected error:", error);
            setLoading(false);
            return false;
         } finally {
            if (fetchTimeoutId) clearTimeout(fetchTimeoutId);
            isReconnectingRef.current = false;
            const shouldKeepGeneration = signal?.aborted && !isTimeout;
            if (!isUnmountingRef.current && !shouldKeepGeneration) {
               removeActiveGeneration(chatHistoryId);
            } else {
               logger.debug(
                  "[StreamReconnect] Skipping removeActiveGeneration on cleanup",
                  {
                     chatHistoryId,
                     isUnmounting: isUnmountingRef.current,
                     aborted: signal?.aborted,
                     isTimeout
                  }
               );
            }
            logger.debug("[StreamReconnect] Cleanup complete", { chatHistoryId });
         }
      },
      [
         setLoading,
         setStreamingResponse,
         setError,
         setCurrentAttachments,
         setHistory,
         selectedBaseKnowledgeId,
         triggerUsageRefresh,
         stopChatStreaming,
         removeActiveGeneration,
         addActiveGeneration,
         translation,
         markCurrentChatUpdated,
         viewGuard
      ]
   );
}
